/*
 * Purty Body — Appstle bundle variant swatches
 *
 * This is a presentation layer only. Appstle's original per-item selects remain
 * the source of truth, so its bundle validation and add-to-cart logic continue
 * to receive the real size and colour selections.
 */

(() => {
  if (window.PurtyAppstleVariantSwatches) return;

  const VERSION = '1.1.0';
  const WIDGET_SELECTOR = '.appstle-volume-discount';
  const SECTION_SELECTOR = '.ab-volume-variant-selector-section';
  const SELECT_SELECTOR = 'select.ab-volume-variant-selector-item-dropdown';
  const INLINE_ADD_BUTTON_SELECTOR = 'button[name="add"][type="submit"]';
  const STICKY_ADD_BUTTON_SELECTOR = '.mobile-sticky-atc__button';
  const CONTROLS_CLASS = 'purty-appstle-master-options';
  const ENHANCED_CLASS = 'purty-appstle-variant-enhanced';
  const MIX_OPEN_CLASS = 'purty-appstle-mix-open';
  let refreshQueued = false;

  const colourValues = {
    'baby pink': '#f5b6cb',
    'black': '#171417',
    'blue pastel': '#b8ddec',
    'brown': '#795548',
    'cream': '#f4ead9',
    'ice blue': '#c7e8f4',
    'nude': '#d7b49e',
    'pink': '#ef8fb1',
    'pink pastel': '#f4c7d7',
    'purple': '#8d65a8',
    'purple pastel': '#d3bce2',
    'red': '#c7354c',
    'soft pink': '#efc3d0',
    'tan': '#c6956d',
    'white': '#ffffff',
    'yellow': '#f0d65d',
  };

  function optionMeta(select) {
    const ariaLabel = select.getAttribute('aria-label') || '';
    const match = ariaLabel.match(/^(.+?),\s*item\s*(\d+)$/i);

    return {
      item: match ? Number(match[2]) : 1,
      name: match ? match[1].trim() : 'Option',
    };
  }

  function normaliseOptionName(name) {
    return String(name || '').trim().toLowerCase();
  }

  function displayOptionName(name) {
    return /colou?r/i.test(name) ? 'Colour' : name;
  }

  function shortOptionValue(name, value) {
    if (/size/i.test(name)) {
      return String(value).split('=')[0].trim();
    }

    return value;
  }

  function colourFor(value) {
    const key = normaliseOptionName(value);
    if (colourValues[key]) return colourValues[key];

    const partialMatch = Object.keys(colourValues).find((colour) => key.includes(colour));
    return partialMatch ? colourValues[partialMatch] : '#eee5e8';
  }

  function activeSection(widget) {
    return (
      widget.querySelector(`.ab-volume-discount-item-selected ${SECTION_SELECTOR}`) ||
      widget.querySelector(SECTION_SELECTOR)
    );
  }

  function selectedTierDetails(widget) {
    const selectedTier = widget.querySelector('.ab-volume-discount-item-selected');
    if (!selectedTier) return null;

    const name = selectedTier
      .querySelector('.ab-volume-discount-item-title-label')
      ?.textContent.trim()
      .replace(/\s+/g, ' ');
    const price = selectedTier
      .querySelector('.ab-volume-discount-item-discount-price')
      ?.textContent.trim()
      .replace(/\s+/g, ' ');

    return name && price ? { name, price } : null;
  }

  function buttonTextTarget(button) {
    return (
      button.querySelector(
        '.single-add-to-cart-button--text, [data-add-to-cart-text], .product-form__submit-label'
      ) || button
    );
  }

  function buttonIsBusyOrUnavailable(button) {
    const label = buttonTextTarget(button).textContent.trim().toLowerCase();

    return (
      button.disabled ||
      button.getAttribute('aria-busy') === 'true' ||
      button.classList.contains('loading') ||
      /sold out|unavailable|adding|added/i.test(label)
    );
  }

  function setCartButtonLabel(button, label) {
    const textTarget = buttonTextTarget(button);

    if (!button.dataset.purtyAppstleDefaultLabel) {
      button.dataset.purtyAppstleDefaultLabel = textTarget.textContent.trim() || 'Add to cart';
    }

    if (buttonIsBusyOrUnavailable(button)) return;

    if (textTarget.textContent.trim() !== label) {
      textTarget.textContent = label;
    }

    if (button.getAttribute('aria-label') !== label) {
      button.setAttribute('aria-label', label);
    }

    button.dataset.purtyAppstleBundleLabel = 'true';
  }

  function restoreCartButtonLabel(button) {
    const defaultLabel = button.dataset.purtyAppstleDefaultLabel;
    if (!defaultLabel || buttonIsBusyOrUnavailable(button)) return;

    const textTarget = buttonTextTarget(button);
    if (textTarget.textContent.trim() !== defaultLabel) {
      textTarget.textContent = defaultLabel;
    }

    button.removeAttribute('data-purty-appstle-bundle-label');
    button.removeAttribute('aria-label');
  }

  function updateCartButtons(widget) {
    const tier = selectedTierDetails(widget);
    const productForm = widget.closest('form');
    const buttons = [];
    const inlineButton = productForm?.querySelector(INLINE_ADD_BUTTON_SELECTOR);

    if (inlineButton) buttons.push(inlineButton);
    document.querySelectorAll(STICKY_ADD_BUTTON_SELECTOR).forEach((button) => buttons.push(button));

    if (!tier) {
      buttons.forEach(restoreCartButtonLabel);
      return;
    }

    const label = `Add ${tier.name} to cart - ${tier.price}`;
    buttons.forEach((button) => setCartButtonLabel(button, label));
  }

  function sectionSelects(section) {
    return Array.from(section.querySelectorAll(SELECT_SELECTOR));
  }

  function optionGroups(section) {
    const groups = new Map();

    sectionSelects(section).forEach((select) => {
      const meta = optionMeta(select);
      const key = normaliseOptionName(meta.name);

      if (!groups.has(key)) {
        groups.set(key, {
          key,
          name: meta.name,
          primary: null,
          selects: [],
        });
      }

      const group = groups.get(key);
      group.selects.push(select);
      if (meta.item === 1 || !group.primary) group.primary = select;
    });

    return Array.from(groups.values()).filter((group) => group.primary);
  }

  function optionSignature(groups) {
    return groups
      .map((group) => {
        const values = Array.from(group.primary.options).map((option) => option.value);
        return `${group.key}:${values.join('|')}`;
      })
      .join('||');
  }

  function setSelectValue(select, value) {
    const matchingOption = Array.from(select.options).find(
      (option) => option.value === value && !option.disabled
    );

    if (!matchingOption || select.value === value) return;

    const valueSetter = Object.getOwnPropertyDescriptor(
      window.HTMLSelectElement.prototype,
      'value'
    )?.set;

    if (valueSetter) {
      valueSetter.call(select, value);
    } else {
      select.value = value;
    }

    select.dispatchEvent(new Event('input', { bubbles: true }));
    select.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function applyValueToAll(widget, optionName, value) {
    const section = activeSection(widget);
    if (!section) return;

    const itemNumbers = sectionSelects(section)
      .map((select) => ({ select, ...optionMeta(select) }))
      .filter((entry) => normaliseOptionName(entry.name) === normaliseOptionName(optionName))
      .map((entry) => entry.item)
      .filter((item, index, items) => items.indexOf(item) === index)
      .sort((a, b) => a - b);

    let index = 0;

    function applyNext() {
      const currentSection = activeSection(widget);
      if (!currentSection || index >= itemNumbers.length) {
        queueRefresh();
        return;
      }

      const itemNumber = itemNumbers[index];
      const target = sectionSelects(currentSection).find((select) => {
        const meta = optionMeta(select);
        return (
          meta.item === itemNumber &&
          normaliseOptionName(meta.name) === normaliseOptionName(optionName)
        );
      });

      if (target) setSelectValue(target, value);
      index += 1;
      window.requestAnimationFrame(applyNext);
    }

    applyNext();
  }

  function buildSwatchButton(widget, group, option) {
    const button = document.createElement('button');
    const isColour = /colou?r/i.test(group.name);

    button.type = 'button';
    button.className = `purty-appstle-swatch${isColour ? ' purty-appstle-colour-swatch' : ''}`;
    button.dataset.purtyValue = option.value;
    button.title = option.textContent.trim();
    button.disabled = option.disabled;
    button.setAttribute('aria-pressed', 'false');
    button.setAttribute(
      'aria-label',
      `${displayOptionName(group.name)} ${option.textContent.trim()}`
    );

    if (isColour) {
      const colour = document.createElement('span');
      colour.className = 'purty-appstle-colour-dot';
      colour.style.setProperty('--purty-option-colour', colourFor(option.value));
      colour.setAttribute('aria-hidden', 'true');
      button.appendChild(colour);
    }

    const label = document.createElement('span');
    label.className = 'purty-appstle-swatch-label';
    label.textContent = shortOptionValue(group.name, option.textContent.trim());
    button.appendChild(label);

    button.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      applyValueToAll(widget, group.name, option.value);
    });

    return button;
  }

  function buildGroup(widget, group) {
    const fieldset = document.createElement('fieldset');
    const heading = document.createElement('legend');
    const headingLabel = document.createElement('span');
    const selectedValue = document.createElement('strong');
    const swatches = document.createElement('div');

    fieldset.className = 'purty-appstle-option-group';
    fieldset.dataset.purtyOptionKey = group.key;

    heading.className = 'purty-appstle-option-heading';
    headingLabel.textContent = `${displayOptionName(group.name)}: `;
    selectedValue.dataset.purtySelectedValue = group.key;
    heading.append(headingLabel, selectedValue);

    swatches.className = 'purty-appstle-swatch-list';
    swatches.setAttribute('role', 'group');
    swatches.setAttribute('aria-label', `Choose ${displayOptionName(group.name)}`);

    Array.from(group.primary.options).forEach((option) => {
      swatches.appendChild(buildSwatchButton(widget, group, option));
    });

    fieldset.append(heading, swatches);
    return fieldset;
  }

  function ensureNativeLabels(section) {
    section
      .querySelectorAll('.ab-volume-variant-selector-item-option')
      .forEach((optionContainer) => {
        if (
          optionContainer.querySelector(
            '.ab-volume-variant-selector-item-option-label, .purty-appstle-native-label'
          )
        ) {
          return;
        }

        const select = optionContainer.querySelector(SELECT_SELECTOR);
        if (!select) return;

        const label = document.createElement('div');
        label.className = 'purty-appstle-native-label';
        label.textContent = displayOptionName(optionMeta(select).name);
        optionContainer.prepend(label);
      });
  }

  function syncControls(section) {
    const controls = section.querySelector(`:scope > .${CONTROLS_CLASS}`);
    if (!controls) return;

    optionGroups(section).forEach((group) => {
      const currentValue = group.primary.value;
      const selectedValues = Array.from(
        new Set(group.selects.map((select) => select.value))
      );
      const hasMixedValues = selectedValues.length > 1;
      const selectedValue = controls.querySelector(
        `[data-purty-selected-value="${group.key}"]`
      );

      if (selectedValue) {
        selectedValue.textContent = hasMixedValues
          ? `Mixed ${displayOptionName(group.name).toLowerCase()}s`
          : currentValue;
      }

      controls
        .querySelectorAll(`[data-purty-option-key="${group.key}"] .purty-appstle-swatch`)
        .forEach((button) => {
          button.setAttribute(
            'aria-pressed',
            String(!hasMixedValues && button.dataset.purtyValue === currentValue)
          );
        });
    });
  }

  function buildMixToggle(widget, section, itemCount) {
    if (itemCount < 2) return null;

    const button = document.createElement('button');
    const label = document.createElement('span');
    const icon = document.createElement('span');

    button.type = 'button';
    button.className = 'purty-appstle-mix-toggle';
    button.setAttribute('aria-expanded', 'false');
    label.textContent = 'Mix colours & sizes';
    icon.className = 'purty-appstle-mix-toggle-icon';
    icon.setAttribute('aria-hidden', 'true');
    icon.textContent = '+';
    button.append(label, icon);

    button.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();

      const isOpen = !section.classList.contains(MIX_OPEN_CLASS);
      section.classList.toggle(MIX_OPEN_CLASS, isOpen);
      widget.dataset.purtyAppstleMixOpen = String(isOpen);
      button.setAttribute('aria-expanded', String(isOpen));
      icon.textContent = isOpen ? '−' : '+';
    });

    return button;
  }

  function enhanceSection(section) {
    const widget = section.closest(WIDGET_SELECTOR);
    if (!widget) return;

    const groups = optionGroups(section);
    if (!groups.length) return;

    const signature = optionSignature(groups);
    const existing = section.querySelector(`:scope > .${CONTROLS_CLASS}`);

    if (existing && existing.dataset.purtySignature === signature) {
      ensureNativeLabels(section);
      syncControls(section);
      return;
    }

    if (existing) existing.remove();

    const controls = document.createElement('div');
    const nativeContainer = section.querySelector(
      ':scope > .ab-volume-variant-selector-container'
    );
    const itemCount = Math.max(...sectionSelects(section).map((select) => optionMeta(select).item));

    controls.className = CONTROLS_CLASS;
    controls.dataset.purtySignature = signature;

    groups.forEach((group) => controls.appendChild(buildGroup(widget, group)));

    const mixToggle = buildMixToggle(widget, section, itemCount);
    if (mixToggle) controls.appendChild(mixToggle);

    section.insertBefore(controls, nativeContainer || section.firstChild);
    section.classList.add(ENHANCED_CLASS);

    const mixIsOpen = itemCount > 1 && widget.dataset.purtyAppstleMixOpen === 'true';
    section.classList.toggle(MIX_OPEN_CLASS, mixIsOpen);

    if (mixToggle) {
      mixToggle.setAttribute('aria-expanded', String(mixIsOpen));
      const icon = mixToggle.querySelector('.purty-appstle-mix-toggle-icon');
      if (icon) icon.textContent = mixIsOpen ? '−' : '+';
    }

    ensureNativeLabels(section);
    syncControls(section);
  }

  function refresh() {
    document.querySelectorAll(WIDGET_SELECTOR).forEach((widget) => {
      const section = activeSection(widget);
      if (section) enhanceSection(section);
      updateCartButtons(widget);
    });
  }

  function queueRefresh() {
    if (refreshQueued) return;
    refreshQueued = true;

    window.requestAnimationFrame(() => {
      refreshQueued = false;
      refresh();
    });
  }

  const observer = new MutationObserver((mutations) => {
    const touchesRelevantControls = mutations.some((mutation) => {
      const target = mutation.target.nodeType === 1 ? mutation.target : mutation.target.parentElement;
      if (
        target?.closest?.(
          `${WIDGET_SELECTOR}, ${INLINE_ADD_BUTTON_SELECTOR}, ${STICKY_ADD_BUTTON_SELECTOR}`
        )
      ) {
        return true;
      }

      return Array.from(mutation.addedNodes).some(
        (node) =>
          node.nodeType === 1 &&
          (node.matches?.(
            `${WIDGET_SELECTOR}, ${INLINE_ADD_BUTTON_SELECTOR}, ${STICKY_ADD_BUTTON_SELECTOR}`
          ) ||
            node.querySelector?.(
              `${WIDGET_SELECTOR}, ${INLINE_ADD_BUTTON_SELECTOR}, ${STICKY_ADD_BUTTON_SELECTOR}`
            ))
      );
    });

    if (touchesRelevantControls) queueRefresh();
  });

  function start() {
    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
    });

    document.addEventListener(
      'change',
      (event) => {
        if (event.target.matches?.(`${WIDGET_SELECTOR} ${SELECT_SELECTOR}`)) {
          queueRefresh();
        }
      },
      true
    );

    document.addEventListener(
      'click',
      (event) => {
        if (event.target.closest?.(`${WIDGET_SELECTOR} .ab-volume-discount-item-container`)) {
          window.setTimeout(queueRefresh, 0);
        }
      },
      true
    );

    refresh();
  }

  window.PurtyAppstleVariantSwatches = {
    refresh,
    version: VERSION,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
